import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { BLUR_SWAP, delayed, getMotionTimeScale, RENDER_EVERY_FRAME, SPRING } from './motionTokens';

// App-wide waiting state for buttons that run an async action. Goes inside the existing
// button (its look is untouched): the label blurs out to a spinner while waiting, then to a
// drawn check when the action succeeded, then back. Spinner and check use the button's own
// text color. The content keeps the idle label's width and height, so the button never jumps.

export type AsyncButtonState = 'idle' | 'loading' | 'success';

const SUCCESS_MS = 900;

export const InlineSpinner: React.FC<{ size?: number; label?: string }> = ({ size = 18, label }) => (
  <motion.svg
    width={size}
    height={size}
    viewBox="0 0 18 18"
    animate={{ rotate: 360 }}
    transition={{ repeat: Infinity, duration: 0.8 * getMotionTimeScale(), ease: 'linear' }}
    role={label ? 'progressbar' : undefined}
    aria-label={label}
    aria-hidden={label ? undefined : true}
    style={{ display: 'block', flexShrink: 0 }}
  >
    <circle cx={9} cy={9} r={7} fill="none" stroke="currentColor" strokeOpacity={0.28} strokeWidth={2} />
    <path d="M9 2 A7 7 0 0 1 16 9" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" />
  </motion.svg>
);

export const InlineCheck: React.FC<{ size?: number }> = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 18 18" aria-hidden style={{ display: 'block', flexShrink: 0 }}>
    <motion.path
      d="M4 9.5 L7.5 13 L14 5.5"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      initial={{ pathLength: 0 }}
      animate={{ pathLength: 1 }}
      transition={delayed(SPRING.snap, 0.1)}
    />
  </svg>
);

// `flash()` shows the success state for a moment; it resolves after `holdMs` so a caller that
// closes a modal on success can `await flash(600)` first and let the check be seen.
export const useSuccessFlash = () => {
  const [success, setSuccess] = useState(false);
  const timer = useRef<number | undefined>(undefined);
  useEffect(() => () => window.clearTimeout(timer.current), []);
  const flash = useCallback((holdMs = 0) => {
    window.clearTimeout(timer.current);
    setSuccess(true);
    timer.current = window.setTimeout(() => setSuccess(false), Math.max(SUCCESS_MS, holdMs) * getMotionTimeScale());
    return new Promise<void>(resolve => window.setTimeout(resolve, holdMs * getMotionTimeScale()));
  }, []);
  return [success, flash] as const;
};

// Success wins over loading so a caller that is still "submitting" while it shows the check
// (before closing a modal) doesn't fall back to the spinner.
export const asyncButtonState = (loading: boolean, success = false): AsyncButtonState => (
  success ? 'success' : loading ? 'loading' : 'idle'
);

interface AsyncButtonContentProps {
  state: AsyncButtonState;
  children: React.ReactNode;
  successLabel?: React.ReactNode;
  iconSize?: number;
  // Accessible name for the spinner while waiting (e.g. "Saving").
  loadingLabel?: string;
}

const AsyncButtonContent: React.FC<AsyncButtonContentProps> = ({
  state,
  children,
  successLabel,
  iconSize = 18,
  loadingLabel,
}) => {
  const idleRef = useRef<HTMLSpanElement | null>(null);
  const [box, setBox] = useState<{ width: number; height: number } | null>(null);

  useLayoutEffect(() => {
    if (state !== 'idle' || !idleRef.current) return;
    const { offsetWidth, offsetHeight } = idleRef.current;
    if (offsetWidth > 0) setBox({ width: offsetWidth, height: offsetHeight });
  }, [state, children]);

  return (
    <span
      style={{
        position: 'relative',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        minWidth: state !== 'idle' && box ? box.width : undefined,
        minHeight: state !== 'idle' && box ? box.height : undefined,
      }}
    >
      <AnimatePresence mode="popLayout" initial={false}>
        <motion.span
          {...RENDER_EVERY_FRAME}
          key={state}
          ref={state === 'idle' ? idleRef : undefined}
          variants={BLUR_SWAP}
          initial="initial"
          animate="animate"
          exit="exit"
          style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}
        >
          {state === 'idle' && children}
          {state === 'loading' && <InlineSpinner size={iconSize} label={loadingLabel} />}
          {state === 'success' && (
            <>
              <InlineCheck size={iconSize} />
              {successLabel}
            </>
          )}
        </motion.span>
      </AnimatePresence>
    </span>
  );
};

// Icon slot that waits with a spinner and lands on the given icon. `popOn` gives one small
// spring pop when the icon key changes to it (e.g. the heart filling on save). No check.
export const AsyncIconSwap: React.FC<{
  loading: boolean;
  icon: React.ReactNode;
  iconKey: string;
  popOn?: string;
  spinnerSize?: number;
  spinnerColor?: string;
}> = ({ loading, icon, iconKey, popOn, spinnerSize = 16, spinnerColor }) => {
  // Pop only when the icon actually changes to `popOn` (or lands on it after waiting), never
  // on first render: a page that loads already-saved shows a still heart.
  const lastKey = useRef(iconKey);
  const wasLoading = useRef(loading);
  const shouldPop = popOn === iconKey && (lastKey.current !== iconKey || (wasLoading.current && !loading));
  useEffect(() => {
    lastKey.current = iconKey;
    wasLoading.current = loading;
  });
  return (
  <span style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
    <AnimatePresence mode="popLayout" initial={false}>
      {loading ? (
        <motion.span
          {...RENDER_EVERY_FRAME}
          key="spin"
          variants={BLUR_SWAP}
          initial="initial"
          animate="animate"
          exit="exit"
          style={{ display: 'inline-flex', color: spinnerColor }}
        >
          <InlineSpinner size={spinnerSize} />
        </motion.span>
      ) : (
        <motion.span
          {...RENDER_EVERY_FRAME}
          key={iconKey}
          variants={BLUR_SWAP}
          initial="initial"
          animate="animate"
          exit="exit"
          style={{ display: 'inline-flex' }}
        >
          <motion.span
            initial={shouldPop ? { scale: 0.6 } : false}
            animate={{ scale: 1 }}
            transition={{ ...SPRING.morph, bounce: 0.35 }}
            style={{ display: 'inline-flex' }}
          >
            {icon}
          </motion.span>
        </motion.span>
      )}
    </AnimatePresence>
  </span>
  );
};

// Text that changes with the shared blur (e.g. "Save" → "Saved").
export const BlurSwapText: React.FC<{ text: string }> = ({ text }) => (
  <AnimatePresence mode="popLayout" initial={false}>
    <motion.span
      {...RENDER_EVERY_FRAME}
      key={text}
      variants={BLUR_SWAP}
      initial="initial"
      animate="animate"
      exit="exit"
      style={{ display: 'inline-block' }}
    >
      {text}
    </motion.span>
  </AnimatePresence>
);

export default AsyncButtonContent;
